"use client";

import Image from "next/image"
import Link from "next/link"
import CTA from "@/components/home/cta"
import { Tab } from '@headlessui/react'
import { useEffect, useState } from 'react'

import Header from "@/components/layout/header";
import Footer from "@/components/layout/footer";
import RelatedLinks from "@/components/shared/related-links";
import FAQSection from "@/components/shared/faq-section";
import { linenFaqs } from "./faqs";

const relatedLinks = [
  {
    href: "/laundry-services-singapore",
    title: "Laundry Services",
    description: "Cloud-managed laundry with RFID tracking for medical scrubs, lab coats and linens.",
  },
  {
    href: "/inventory-management-solution",
    title: "RFID Inventory Management",
    description: "UHF RFID tags, smart cabinets and cloud analytics for any tagged consumable.",
  },
  {
    href: "/order-management-platform",
    title: "Order Management Platform",
    description: "End-to-end ordering for uniform, workwear and PPE — integrates with linen management.",
  },
];

const features = [
  {
    title: '1. Tag & register garments with RFID',
    description:
      "Keep track of everyone's salaries and whether or not they've been paid. Direct deposit not supported.",
    image: "/images/linen-management-system/tag-register-rfid.jpg",
  },
  {
    title: '2. Deploy smart cabinets',
    description:
      "All of your receipts organized into one place, as long as you don't mind typing in the data by hand.",
    image: "/images/linen-management-system/deploy-smart-cabinets.jpg",
  },
  {
    title: '3. Track & manage transactions in one place',
    description:
      "We only sell our software to companies who don't deal with VAT at all, so technically we do all the VAT stuff they need.",
    image: "/images/linen-management-system/track-transactions.png",
  },
]

const featureDescriptions = [
  {
    name: 'MF UHF RFID Tag',
    description:
      'MF RFID tags are sewn into garments and then registered into the MF platform. RFID tags are fundamental to track each and every garment. Once attached, every piece of workwear is accounted for and can be tracked and traced when delivered new, sent for washing or delivered from laundry.',
  },
  {
    name: 'Textile cabinets & laundry bins',
    description:
      'Smart cabinets track deposits and withdrawals by each user. Real-time tracking updates laundry partners when full. Textile cabinets track and manage the issue and return of workwear. Lockers have inbuilt RFID readers that automatically records garments removed or returned and syncs all transactions with MF’s platform.',
  },
  {
    name: 'Door stations & room solutions',
    description:
      'Room & door solutions are best suited for high volumes of traffic. They are carefully planned Entries & exits that have readers deployed to identify wearers & garments taken. Room solutions focus on speed, reliability and real-time tracking of inventory.',
  }
]

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}


export default function PageBody() {

  let [tabOrientation, setTabOrientation] = useState('horizontal')

  useEffect(() => {
    let lgMediaQuery = window.matchMedia('(min-width: 1024px)')

    function onMediaQueryChange({ matches }) {
      setTabOrientation(matches ? 'vertical' : 'horizontal')
    }

    onMediaQueryChange(lgMediaQuery)
    lgMediaQuery.addEventListener('change', onMediaQueryChange)

    return () => {
      lgMediaQuery.removeEventListener('change', onMediaQueryChange)
    }
  })

  return (
    <>
      <Header />
      <main>
      <div className="bg-white py-24 sm:py-32 relative">
        <div className="mx-auto max-w-3xl text-center px-1">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl"> MF Linen Management System </h1>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            <b> Deploy linen management systems. </b>
            MF is the all-in-one platform that helps you deploy IoT & RFID technology to enable real-time inventory availability and visibility.
            <Link className="text-lg font-semibold leading-6 text-blue-600" href="/contact-us"> Get Started </Link>
          </p>
          
        </div>
        {/* <Tabs/> */}
        <section
          id="features"
          aria-label="Automated, real-time textile management"
          className="relative overflow-hidden bg-white py-24 mx-auto"
        >
          <div className="relative">
            {/* <b> Deploy linen management systems. </b> */}
            <Tab.Group
              as="div"
              className="grid grid-cols-1 items-center gap-y-2 gap-x-4 sm:gap-y-6 lg:grid-cols-12 lg:pt-0"
              vertical={tabOrientation === 'vertical'}
            >
              {({ selectedIndex }) => (
                <>
                  <div className="flex overflow-x-auto pb-4 sm:mx-0 sm:overflow-visible sm:pb-0 lg:col-span-5 justify-center">
                    <div className="xl:px-24 lg:px-12 sm:px-12 px-4">
                      <h3 className="mb-5 text-3xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                        Automated, real-time textile management
                      </h3>
                      {/* <h3 className="mb-5 text-xl  sm:text-3xl font-bold text-gray-900 text-center"> Automated, real-time textile management </h3> */}
                      <Tab.List className="relative z-10 flex whitespace-normal gap-x-4 px-4 sm:mx-auto sm:px-0 lg:mx-0 lg:block lg:gap-x-0 lg:gap-y-1 lg:whitespace-normal">
                        {features.map((feature, featureIndex) => (
                          <div
                            key={feature.title}
                            className={classNames(
                              'group relative rounded-xl py-1 px-4 lg:p-6 lg:my-5',
                              selectedIndex === featureIndex
                                ? 'bg-black/10 ring-1 ring-inset ring-black/10'
                                : ' bg-black/5 hover:bg-black/10'
                            )}
                          >
                            <h3>
                              <Tab
                                className={classNames(
                                  'font-display text-sm sm:text-lg [&:not(:focus-visible)]:focus:outline-none outline-none',
                                  selectedIndex === featureIndex
                                    ? 'text-gray-900'
                                    : 'hover:text-gray-900 text-gray-400'
                                )}
                              >
                                <span className="absolute inset-0 rounded-xl" />
                                {feature.title}
                              </Tab>
                            </h3>
                          </div>
                        ))}
                      </Tab.List>
                    </div>
                  </div>
                  <Tab.Panels className="lg:col-span-7">
                    {features.map((feature) => (
                      <Tab.Panel key={feature.title} unmount={false}>
                        {/* <div className="relative sm:px-6 lg:hidden">
                          <div className="absolute -inset-x-4 top-[-6.5rem] bottom-[-4.25rem] bg-white/10 ring-1 ring-inset ring-white/10 sm:inset-x-0 sm:rounded-t-xl" />
                          <p className="relative mx-auto max-w-2xl text-base text-white sm:text-center">
                            {feature.description}
                          </p>
                        </div> */}
                        <div className="mt-10 overflow-hidden rounded-xl bg-slate-50 shadow-xl shadow-blue-900/20 sm:w-auto lg:mt-0">
                          <Image
                            className="w-full"
                            src={feature.image}
                            alt={feature.title}
                            priority
                            width={1024}
                            height={1024}
                            sizes="(min-width: 1024px) 67.8125rem, (min-width: 640px) 100vw, 45rem"
                          />
                        </div>
                      </Tab.Panel>
                    ))}
                  </Tab.Panels>
                </>
              )}
            </Tab.Group>
          </div>
        </section>
        
        <div className="bg-gray-100 py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <h3 className="mb-5 text-3xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              Choose the best solution for your workwear requirements
            </h3>
            <div className="mx-auto grid max-w-2xl grid-cols-1 gap-8 overflow-hidden lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {featureDescriptions.map((item) => (
                <div key={item.name}>
                  <p className="mt-6 text-lg font-semibold leading-8 tracking-tight text-gray-900">{item.name}</p>
                  <p className="mt-2 text-base leading-7 text-gray-600">{item.description}</p>
                </div>
              ))}
            </div> 
          </div>
        </div>

        <FAQSection items={linenFaqs} />
        <RelatedLinks links={relatedLinks} />
        <CTA/>
      </div>
    </main>
      <Footer />
    </>
  )
}